"use client";

import * as React from "react";
import { X } from "lucide-react";

import { MagnifyingGlassIcon } from "./icons";
import { cn } from "@punchup/utils/cn";

export interface SearchInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  onClear?: () => void;
  size?: "sm" | "md" | "lg";
  variant?: "default" | "compact";
}

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    { onClear, value, size = "md", variant = "default", className, ...props },
    ref
  ) => {
    // Size variants
    const sizeClasses = {
      sm: "text-sm px-9 py-2 h-9",
      md: "text-[16px] px-11 py-3 h-12",
      lg: "text-lg px-12 py-4 h-14",
    };

    // Icon sizes based on input size
    const iconSizes = {
      sm: { width: 16, height: 16, leftPadding: "pl-3" },
      md: { width: 20, height: 20, leftPadding: "pl-4" },
      lg: { width: 24, height: 24, leftPadding: "pl-4" },
    };

    const currentSize = iconSizes[size];

    return (
      <div className="relative">
        <input
          type="text"
          value={value}
          className={cn(
            "items-center pr-4 appearance-none focus:outline-none text-primary-blue w-full font-sans leading-6 placeholder:font-light placeholder:text-tertiary-blue border border-secondary-blue rounded-[10px] disabled:opacity-50",
            sizeClasses[size],
            className
          )}
          ref={ref}
          {...props}
        />
        <div
          className={cn(
            "absolute inset-y-0 left-0 flex items-center",
            currentSize.leftPadding
          )}
        >
          <MagnifyingGlassIcon
            width={currentSize.width}
            height={currentSize.height}
            className="stroke-tertiary-blue"
          />
        </div>
        {onClear && (
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center px-3"
            onClick={onClear}
            aria-label="Clear search"
            disabled={
              !value || (typeof value === "string" && value.length === 0)
            }
            aria-disabled={
              !value || (typeof value === "string" && value.length === 0)
            }
          >
            <X
              size={size === "sm" ? 14 : size === "lg" ? 20 : 16}
              className={cn(
                "transition-[stroke] ease-in-out duration-200",
                value && typeof value === "string" && value.length > 0
                  ? "stroke-primary-blue"
                  : "opacity-0 stroke-transparent"
              )}
            />
          </button>
        )}
      </div>
    );
  }
);

SearchInput.displayName = "SearchInput";

export default SearchInput;
